import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";

// FAQ content (SEBI UPI handle related)
const faqs = [
  {
    q: "Is it compulsory for the investors to use the new handle only?",
    a: "The investors can choose their preferred mode of payment, such as UPI, IMPS, NEFT, RTGS, or Cheques. If an investor opts to use UPI for the payment to registered intermediaries, then they have to do so only using the new UPI IDs allotted to registered intermediaries.",
  },
  {
    q: "What should I check while making payment using the new UPI IDs/ QR Code?",
    a: "Investors need to keep following things into consideration:",
    points: [
      'The UPI ID should properly show the name of the intermediary, followed by the short abbreviation of their category — for example "brk" for Brokers, "mf" for Mutual Funds to the left of the character.',
      'On the right side, the new and exclusive handle "@valid" should be present, followed by the bank name.',
      "On the confirmation screen, the app should show a white thumbs-up icon inside a green triangle.",
      "The QR code generated using the utility will have a white thumbs-up icon inside a green triangle. It will also display the UPI ID just below the QR code.",
    ],
  },
  {
    q: "Do investors also need to obtain new UPI handles to transact in the securities market?",
    a: "No, the new UPI IDs are only for intermediaries to obtain and investors can continue to use their existing UPI IDs.",
  },
  {
    q: "Whom to approach if my transaction/ payment fails with the new UPI ID?",
    a: "The secure validated UPI ID of intermediaries will use the same banking channel as the earlier generic UPI handles. In case of any technical difficulty, investors are requested to approach their respective bank.",
  },
];

function FaqItem({ faq, index, isOpen, onToggle }) {
  return (
    <div
      className={`rounded-2xl overflow-hidden bg-white transition-all duration-300 border ${
        isOpen
          ? "border-[#1e6091]/30 shadow-[0_8px_30px_-12px_rgba(30,96,145,0.25)]"
          : "border-zinc-200 hover:border-[#1e6091]/30"
      }`}
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 sm:gap-4 text-left px-4 sm:px-5 md:px-7 py-4 sm:py-5"
      >
        {/* number badge */}
        <span
          className={`grid place-items-center w-8 h-8 sm:w-9 sm:h-9 flex-shrink-0 rounded-xl text-xs sm:text-sm font-black transition-colors duration-300 ${
            isOpen ? "bg-[#1e6091] text-white" : "bg-[#1e6091]/10 text-[#1e6091]"
          }`}
        >
          {String(index + 1).padStart(2, "0")}
        </span>

        <span className={`flex-1 text-sm sm:text-[15px] md:text-base font-bold transition-colors ${isOpen ? "text-[#1e6091]" : "text-zinc-900"}`}>
          {faq.q}
        </span>

        {/* chevron */}
        <span
          className={`grid place-items-center w-7 h-7 flex-shrink-0 rounded-full border transition-all duration-300 ${
            isOpen ? "border-[#1e6091] text-[#1e6091] rotate-180" : "border-zinc-300 text-zinc-400"
          }`}
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </span>
      </button>

      <div className={`grid transition-all duration-300 ${isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
        <div className="overflow-hidden">
          <div className="px-4 sm:px-5 md:px-7 pb-5 sm:pb-6 md:pl-[4.75rem]">
            <div className="border-l-2 border-[#1e6091]/15 pl-4 md:border-l-0 md:pl-0">
              <p className="text-sm md:text-[15px] text-zinc-600 leading-relaxed">{faq.a}</p>
              {faq.points && (
                <ol className="mt-3 space-y-2.5 list-none">
                  {faq.points.map((p, i) => (
                    <li key={i} className="flex gap-3 text-sm md:text-[15px] text-zinc-600 leading-relaxed">
                      <span className="grid place-items-center w-5 h-5 flex-shrink-0 mt-0.5 rounded-full bg-[#1e6091]/10 text-[#1e6091] text-[11px] font-bold">
                        {i + 1}
                      </span>
                      <span>{p}</span>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Renamed this variable so it doesn't conflict with the component props
const defaultPackagesData = [
  // ─── Monthly package (hidden for now) ───
  // {
  //   title: 'Index Options 1 Month',
  //   price: '₹12,500',
  //   taxInfo: '+ 18% GST',
  //   features: [
  //     'One research alert per day through WhatsApp broadcast.',
  //     'Get trade alert of Bank Nifty/Nifty/Finnifty Option per day.',
  //     'Get trade alert of Bankex/Sensex/Midcap every week.',
  //     'Get Expiry Special research alerts on every weekly expiry and BTST.',
  //     'Get well-researched targets and stop-loss on every research alert.',
  //   ],
  //   riskLevel: 'High to Very High',
  // },
  // ─── 3 Month package (hidden for now) ───
  // {
  //   title: 'Index Options Quality  Package 3 Month',
  //   price: '₹37,500',
  //   taxInfo: '+ 18% GST',
  //   features: [
  //     'One research alert per day through WhatsApp broadcast.',
  //     'Get trade alert of Bank Nifty/Nifty/Finnifty Option per day.',
  //     'Get trade alert of Bankex/Sensex/Midcap every week.',
  //     'Get Expiry Special research alerts on every weekly expiry and BTST.',
  //     'Get well-researched targets and stop-loss on every research alert.',
  //   ],
  //   riskLevel: 'High to Very High',
  // },
  {
    title: 'Index Options Yearly Package',
    price: '₹1,51,000',
    taxInfo: '+ 18% GST',
    features: [
      'One to two research alerts per day through WhatsApp broadcast.',
      'Get trade alert of Bank Nifty/Nifty/Finnifty Option per day.',
      'Get trade alert of Bankex/Sensex/Midcap every week.',
      'Get Expiry Special research alerts on every weekly expiry and BTST.',
      'Get well-researched targets and stop-loss on every research alert.',
    ],
    riskLevel: 'High to Very High',
  },
];

export default function PackagesPage({ packagesData = [] }) {
  const navigate = useNavigate();

  // FIX: If no props are passed, it will now successfully load the default data above
  const data = packagesData.length > 0 ? packagesData : defaultPackagesData;

  const goToContact = () => navigate("/contact");

  const [openFaq, setOpenFaq] = useState(0);

  // Single-package layout — show the (only) active package across a wide card.
  const pkg = data[0];

  return (
    <section className="relative bg-white py-16 sm:py-20 md:py-24 px-4 sm:px-6 font-sans overflow-hidden selection:bg-[#1e6091] selection:text-white">

    <Helmet>
  <title>Service offerings | Option Bricks</title>

</Helmet>

      {/* --- HEADER SECTION --- */}
      <div className="relative max-w-3xl mx-auto text-center mb-12 md:mb-16">
        <span className="inline-block px-4 py-1.5 rounded-full bg-[#1e6091]/[0.08] border border-[#1e6091]/20 text-[#1e6091] text-[11px] sm:text-xs font-bold uppercase tracking-widest mb-5">
          Service Offerings
        </span>
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-zinc-900 tracking-tight mb-4 sm:mb-5 leading-tight">
          Our Premium Yearly Package
        </h2>
        <p className="text-base sm:text-lg md:text-xl text-zinc-500 font-medium leading-relaxed">
          One complete plan built for serious traders — daily research alerts,
          weekly updates, and well-researched targets with clear risk management.
        </p>
      </div>

      {/* --- SINGLE PACKAGE (full-width split card) --- */}
      <div className="relative max-w-6xl mx-auto">

        {/* gradient border wrapper */}
        <div className="relative rounded-[2rem] p-[1.5px] bg-gradient-to-br from-[#1e6091]/30 via-zinc-200 to-[#1e6091]/30 shadow-[0_10px_30px_-12px_rgba(15,41,66,0.18)]">
          <div className="grid lg:grid-cols-[1.05fr_1fr] rounded-[2rem] overflow-hidden bg-white">

            {/* LEFT — Brand panel: title, price, CTA */}
            <div className="relative p-7 sm:p-9 md:p-12 text-white overflow-hidden flex flex-col justify-center"
              style={{ backgroundImage: 'linear-gradient(150deg, #0b2236 0%, #14456b 50%, #1e6091 100%)' }}
            >
              {/* fine grid texture */}
              <div className="absolute inset-0 opacity-[0.07] pointer-events-none"
                style={{
                  backgroundImage:
                    'linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)',
                  backgroundSize: '34px 34px',
                }}
              ></div>
              {/* glow orb */}
              <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-[#3b82c4]/30 blur-3xl pointer-events-none"></div>

              <div className="relative">
                <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 text-blue-50 text-[11px] font-bold uppercase tracking-[0.2em] py-1.5 px-4 rounded-full backdrop-blur-sm mb-8">
                  <svg className="w-3.5 h-3.5 text-blue-200" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l2.4 7.4H22l-6 4.6 2.3 7.4-6.3-4.6L5.7 21l2.3-7.4-6-4.6h7.6z"/></svg>
                  Best Value
                </div>

                <h3 className="text-2xl sm:text-3xl md:text-[2.6rem] font-black tracking-tight mb-4 sm:mb-5 leading-[1.15] text-white">
                  {pkg.title}
                </h3>

                <span className="inline-flex items-center gap-1.5 px-3 py-1 text-[11px] sm:text-xs font-bold uppercase tracking-wider rounded-full bg-white/10 border border-white/20 text-blue-100 mb-8 sm:mb-10">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-300 animate-pulse"></span>
                  Risk: {pkg.riskLevel}
                </span>

                <div className="flex flex-wrap items-end gap-x-3 gap-y-1 mb-1">
                  <span className="text-4xl sm:text-5xl md:text-6xl font-black tracking-tighter">
                    {pkg.price}
                  </span>
                  <span className="text-sm font-medium text-blue-200/80 mb-1.5 sm:mb-2">
                    {pkg.taxInfo}
                  </span>
                </div>
                <p className="text-xs font-medium text-blue-200/70 mb-9 tracking-wide">
                  Billed annually · Valid for 12 months
                </p>

                <button onClick={goToContact} className="group w-full sm:w-auto inline-flex items-center justify-center gap-2 px-9 py-4 rounded-xl font-bold text-base bg-white text-[#0f2942] hover:bg-blue-50 shadow-sm hover:-translate-y-0.5 transition-all duration-300">
                  Subscribe Now
                  <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 6l6 6-6 6" />
                  </svg>
                </button>
              </div>
            </div>

            {/* RIGHT — Features panel */}
            <div className="relative p-7 sm:p-9 md:p-12 flex flex-col justify-center bg-white">

              {/* laptop mockup — sample research alerts */}
              <div className="mb-9">
                <div className="relative rounded-t-xl bg-[#0f2942] p-2 shadow-sm">
                  <div className="absolute top-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-white/30"></div>
                  <div className="rounded-md overflow-hidden bg-zinc-50">
                    {/* app top bar */}
                    <div className="flex items-center gap-1.5 px-3 py-2 bg-white border-b border-zinc-100">
                      <span className="w-2 h-2 rounded-full bg-red-400"></span>
                      <span className="w-2 h-2 rounded-full bg-amber-400"></span>
                      <span className="w-2 h-2 rounded-full bg-emerald-400"></span>
                      <span className="grid place-items-center w-4 h-4 rounded bg-[#1e6091] text-white text-[8px] font-black ml-2">OB</span>
                      <span className="text-[11px] font-bold text-zinc-700">Subscription Plan</span>
                    </div>
                    {/* price summary */}
                    <div className="p-3.5">
                      <div className="rounded-lg bg-white border border-zinc-100 px-3.5 py-3">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-[#1e6091] mb-1.5">
                          {pkg.title}
                        </p>
                        <div className="flex items-end gap-2 mb-2.5">
                          <span className="text-2xl font-black tracking-tight text-zinc-900">{pkg.price}</span>
                          <span className="text-[10px] font-medium text-zinc-400 mb-1">{pkg.taxInfo} / year</span>
                        </div>
                        <div className="space-y-1.5">
                          <div className="flex items-center justify-between text-[11px]">
                            <span className="text-zinc-500">Daily research alerts</span>
                            <span className="font-semibold text-zinc-800">1–2 / day</span>
                          </div>
                          <div className="flex items-center justify-between text-[11px]">
                            <span className="text-zinc-500">Validity</span>
                            <span className="font-semibold text-zinc-800">12 months</span>
                          </div>
                          <div className="flex items-center justify-between text-[11px]">
                            <span className="text-zinc-500">Targets & stop-loss</span>
                            <span className="font-semibold text-emerald-600">Included ✓</span>
                          </div>
                        </div>
                        <button onClick={goToContact} className="mt-3 w-full rounded-md bg-[#1e6091] hover:bg-[#185076] text-white text-[11px] font-bold text-center py-1.5 transition-colors">
                          Subscribe Now
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                {/* base / hinge */}
                <div className="relative mx-auto h-2.5 w-[108%] -ml-[4%] rounded-b-lg bg-gradient-to-b from-zinc-300 to-zinc-400">
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-16 h-1 rounded-b bg-zinc-500/60"></div>
                </div>
              </div>

              <div className="flex items-center gap-2 mb-8">
                <span className="h-px w-8 bg-[#1e6091]"></span>
                <h4 className="font-bold text-xs uppercase tracking-[0.2em] text-[#1e6091]">
                  What's included
                </h4>
              </div>

              <ul className="space-y-4">
                {pkg.features.map((feature, idx) => (
                  <li key={idx} className="group flex items-start gap-4 text-[15px] font-medium leading-relaxed rounded-xl -mx-2 px-2 py-1.5 hover:bg-[#1e6091]/[0.04] transition-colors">
                    <span className="grid place-items-center w-6 h-6 flex-shrink-0 rounded-md bg-[#1e6091] text-white mt-0.5 shadow-sm">
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </span>
                    <span className="text-zinc-700">{feature}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-9 pt-6 border-t border-zinc-100 flex items-start gap-3">
                <svg className="w-5 h-5 flex-shrink-0 text-[#1e6091] mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm text-zinc-500 leading-relaxed">
                  All alerts are delivered via WhatsApp broadcast with researched
                  targets and stop-loss. Investments are subject to market risk.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* --- FAQ SECTION --- */}
      <div className="relative max-w-4xl mx-auto mt-16 sm:mt-20 md:mt-24">
        <div className="text-center mb-9 md:mb-12">
          <span className="inline-block px-4 py-1.5 rounded-full bg-[#1e6091]/[0.08] border border-[#1e6091]/20 text-[#1e6091] text-[11px] sm:text-xs font-bold uppercase tracking-widest mb-5">
            FAQs
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-zinc-900 tracking-tight">
            Frequently Asked Questions
          </h2>
        </div>

        <div className="space-y-3.5">
          {faqs.map((faq, idx) => (
            <FaqItem
              key={idx}
              faq={faq}
              index={idx}
              isOpen={openFaq === idx}
              onToggle={() => setOpenFaq(openFaq === idx ? -1 : idx)}
            />
          ))}
        </div>

        {/* still have questions CTA */}
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 rounded-2xl px-6 py-6 text-white"
          style={{ backgroundImage: "linear-gradient(135deg, #0f2942 0%, #1e6091 100%)" }}
        >
          <div className="text-center sm:text-left">
            <p className="font-bold text-lg">Still have questions?</p>
            <p className="text-blue-100/80 text-sm">Our team is happy to help you get started.</p>
          </div>
          <button
            onClick={goToContact}
            className="group inline-flex items-center gap-2 px-7 py-3 rounded-xl font-bold text-sm bg-white text-[#0f2942] hover:bg-blue-50 transition-all duration-300 whitespace-nowrap"
          >
            Contact Us
            <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </button>
        </div>
      </div>
    </section>
  );
}